import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { PercentWithUnitAndLock } from 'components/shared/Percent/PercentWithUnitAndLock';
import type { LockablePercentFieldData, LockablePercentFieldMetadata } from 'models/forms/fields';
import { FC, useCallback } from 'react';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const LockablePercentField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [lockValue, setLockValue] = useData<LockablePercentFieldData>(id);
  const { isLocked, showValue, nbDecimals, isHighlighted, decimalPadding } =
    useFieldMetadata<LockablePercentFieldMetadata>(id)!;
  const translationKey = useFieldTranslationKey(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const highlightColor = useHighlightColor();

  const onBlur = useCallback(
    (val: number | undefined) => {
      const newValue = val && Number.isFinite(val) ? val : 0; // TODO Replace by null ?
      if (newValue !== lockValue?.value) {
        setLockValue({ value: newValue, customized: true });
      }
    },
    [lockValue, setLockValue],
  );

  const onLockChanged = useCallback(
    (locked: boolean) => {
      if (lockValue) {
        setLockValue(locked ? null : { value: lockValue.value, customized: true });
      } else {
        setLockValue(locked ? null : { value: 0, customized: true });
      }
    },
    [lockValue, setLockValue],
  );

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <PercentWithUnitAndLock
        e2e={id}
        unit="%"
        value={showValue ? (lockValue?.value ?? null) : null}
        isLocked={isLocked}
        onBlur={onBlur}
        precision={nbDecimals}
        onLockChanged={onLockChanged}
        hasError={isFieldInvalid}
        highlightColor={isHighlighted ? highlightColor : undefined}
        decimalPadding={decimalPadding}
        tabIndex={tabIndex}
      />
    </BlockOrientationFormGroup>
  );
};

LockablePercentField.displayName = 'LockablePercentField';
